/**
 * Reading the glyphs of a station label (labels.ts finds them).
 *
 * Each glyph is drawn into a small grid measured in the line's own cap height and baseline — so a
 * full stop stays small at the foot, a hyphen at mid-height, a 1 narrow — as ink density sampled
 * from the plan's lightness (not the thresholded ink, which loses the shape of 8 px digits). A
 * small network (weights in ocr-model.ts, trained by scripts/ocr-train.mjs on glyphs cut from real
 * plans and on labels drawn in many typefaces) then says which character it is, and how sure.
 */
import { lumaAt, type Luma } from './ink.ts';
import type { TextLine } from './labels.ts';
import { MODEL } from './ocr-model.ts';

/** The characters read. `~` stands for anything else (a stray mark, a letter outside the set). */
export const CHARS = '0123456789+.:-\'/()ABCDEFGHIJKLMNOPQRSTUVWXYZ℄~';
export const GW = 16;
export const GH = 20;
const EXTRA = 5;
export const FEATURES = GW * GH + EXTRA;
/** The grid spans this far above and below the baseline, in cap heights. */
const ABOVE = 1.3,
  BELOW = 0.35;

export interface GlyphBox {
  x0: number; // image px
  y0: number;
  x1: number; // exclusive
  y1: number;
  parts: number; // shapes it is made of (a colon: 2)
}

export interface LineMetrics {
  H: number; // cap height, image px
  base: number; // baseline, image px
  paper: number; // lightness of the paper round the line
  dark: number; // lightness of its ink
}

/** The lightness of the paper and of the ink over a box of the plan (a line of text). */
export function toneOf(l: Luma, x0: number, y0: number, x1: number, y1: number): { paper: number; dark: number } {
  const hist = new Uint32Array(256);
  let n = 0;
  for (let y = Math.max(0, Math.floor(y0)); y < Math.min(l.h, Math.ceil(y1)); y++)
    for (let x = Math.max(0, Math.floor(x0)); x < Math.min(l.w, Math.ceil(x1)); x++) {
      hist[l.data[y * l.w + x]!]!++;
      n++;
    }
  const at = (q: number) => {
    let acc = 0;
    for (let v = 0; v < 256; v++) {
      acc += hist[v]!;
      if (acc >= q * n) return v;
    }
    return 255;
  };
  const paper = at(0.8),
    dark = at(0.02);
  return { paper, dark: Math.min(dark, paper - 40) };
}

/** A found line's glyphs and measures in image px (the line was found in working px, at scale k). */
export function lineGlyphs(l: Luma, line: TextLine, k: number): { boxes: GlyphBox[]; metrics: LineMetrics } {
  const boxes = line.glyphs.map((g) => ({ x0: g.minX / k, y0: g.minY / k, x1: (g.maxX + 1) / k, y1: (g.maxY + 1) / k, parts: g.ids.length }));
  const H = line.H / k,
    base = line.base / k;
  const tone = toneOf(l, line.minX / k, line.minY / k, (line.maxX + 1) / k, (line.maxY + 1) / k);
  return { boxes, metrics: { H, base, ...tone } };
}

/** The glyph as the network sees it: GW × GH ink densities, then its proportions. */
export function glyphFeatures(l: Luma, g: GlyphBox, m: LineMetrics, out: Float32Array = new Float32Array(FEATURES)): Float32Array {
  const top = m.base - ABOVE * m.H;
  const cellH = ((ABOVE + BELOW) * m.H) / GH;
  const gw = g.x1 - g.x0;
  const cellW = gw <= GW * cellH ? cellH : gw / GW;
  const cx = (g.x0 + g.x1) / 2;
  const left = cx - (GW / 2) * cellW;
  const span = Math.max(1, m.paper - m.dark);
  const SUB = 3;
  for (let r = 0; r < GH; r++) {
    for (let c = 0; c < GW; c++) {
      let acc = 0;
      for (let sy = 0; sy < SUB; sy++)
        for (let sx = 0; sx < SUB; sx++) {
          const x = left + (c + (sx + 0.5) / SUB) * cellW,
            y = top + (r + (sy + 0.5) / SUB) * cellH;
          // only the glyph's own box: a neighbour close by is not part of it
          if (x < g.x0 - 0.25 || x > g.x1 + 0.25 || y < g.y0 - 0.25 || y > g.y1 + 0.25) continue;
          const v = (m.paper - lumaAt(l, x, y)) / span;
          acc += v < 0 ? 0 : v > 1 ? 1 : v;
        }
      out[r * GW + c] = acc / (SUB * SUB);
    }
  }
  const o = GW * GH;
  out[o] = Math.min(3, gw / m.H) / 3;
  out[o + 1] = (m.base - g.y1) / m.H; // how far its foot stands off the baseline
  out[o + 2] = (m.base - g.y0) / m.H / 1.5; // how high it reaches
  out[o + 3] = Math.min(3, g.parts) / 3;
  out[o + 4] = Math.min(2, (g.y1 - g.y0) / m.H) / 2;
  return out;
}

export interface Reading {
  ch: string;
  p: number; // the network's probability for it
  second: string;
  p2: number;
}

/** A small multilayer network: FEATURES → hidden (ReLU) → CHARS (softmax). */
class Net {
  readonly w1: Float32Array;
  readonly b1: Float32Array;
  readonly w2: Float32Array;
  readonly b2: Float32Array;
  readonly hidden: number;
  constructor(m: { hidden: number; w1: Float32Array; b1: Float32Array; w2: Float32Array; b2: Float32Array }) {
    this.hidden = m.hidden;
    this.w1 = m.w1;
    this.b1 = m.b1;
    this.w2 = m.w2;
    this.b2 = m.b2;
  }
  probs(x: Float32Array): Float32Array {
    const H = this.hidden,
      C = CHARS.length;
    const h = new Float32Array(H);
    for (let j = 0; j < H; j++) {
      let s = this.b1[j]!;
      const o = j * FEATURES;
      for (let i = 0; i < FEATURES; i++) s += this.w1[o + i]! * x[i]!;
      h[j] = s > 0 ? s : 0;
    }
    const z = new Float32Array(C);
    let max = -Infinity;
    for (let c = 0; c < C; c++) {
      let s = this.b2[c]!;
      const o = c * H;
      for (let j = 0; j < H; j++) s += this.w2[o + j]! * h[j]!;
      z[c] = s;
      if (s > max) max = s;
    }
    let sum = 0;
    for (let c = 0; c < C; c++) sum += z[c] = Math.exp(z[c]! - max);
    for (let c = 0; c < C; c++) z[c]! /= sum;
    return z;
  }
}

let net: Net | null | undefined;
function model(): Net | null {
  if (net !== undefined) return net;
  net = MODEL ? new Net(decodeModel(MODEL)) : null;
  return net;
}

/** The weights as stored: int8, one scale per tensor, base64. */
function decodeModel(m: { hidden: number; scales: number[]; data: string }): { hidden: number; w1: Float32Array; b1: Float32Array; w2: Float32Array; b2: Float32Array } {
  const bin = atob(m.data);
  const bytes = new Int8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = (bin.charCodeAt(i) << 24) >> 24;
  const H = m.hidden,
    C = CHARS.length;
  const sizes = [H * FEATURES, H, C * H, C];
  const out: Float32Array[] = [];
  let o = 0;
  sizes.forEach((n, t) => {
    const a = new Float32Array(n);
    for (let i = 0; i < n; i++) a[i] = bytes[o + i]! * m.scales[t]!;
    out.push(a);
    o += n;
  });
  return { hidden: H, w1: out[0]!, b1: out[1]!, w2: out[2]!, b2: out[3]! };
}

/** Whether the reader has its model (a plan is read only when it does). */
export function ocrReady(): boolean {
  return model() !== null;
}

/** Read one glyph: the likeliest character and the runner-up. */
export function readGlyph(features: Float32Array): Reading {
  const n = model();
  if (!n) return { ch: '~', p: 0, second: '~', p2: 0 };
  const p = n.probs(features);
  let a = 0,
    b = -1;
  for (let c = 1; c < p.length; c++) {
    if (p[c]! > p[a]!) {
      b = a;
      a = c;
    } else if (b < 0 || p[c]! > p[b]!) b = c;
  }
  return { ch: CHARS[a]!, p: p[a]!, second: CHARS[b]!, p2: p[b]! };
}

/** Probabilities for every character (for the reader of a whole label, which weighs readings against its syntax). */
export function glyphProbs(features: Float32Array): Float32Array | null {
  return model()?.probs(features) ?? null;
}
